/**
 * Article ScrollSpy - Custom ScrollSpy Implementation
 * 
 * This script:
 * 1. Adds IDs to article headings based on Liquid-generated data
 * 2. Custom scroll spy that activates based on what's at the top of viewport
 * 3. Handles mobile TOC toggle
 * 4. Handles Article section expand/collapse
 */

(function() {
  'use strict';

  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    console.log('[ScrollSpy] Initializing...');
    
    const tocNav = document.querySelector('#article-toc-nav');
    const contentArea = document.querySelector('.rr-article-content');
    
    if (!tocNav || !contentArea) {
      console.warn('[ScrollSpy] TOC nav or content area not found');
      return;
    }

    console.log('[ScrollSpy] Found TOC nav and content area');

    // Add IDs to headings in the content
    addHeadingIds(contentArea, tocNav);
    
    // Initialize Bootstrap ScrollSpy
    initScrollSpy(tocNav);
    
    // Setup smooth scroll for TOC links
    setupSmoothScroll(tocNav);
    
    // Setup mobile toggle
    setupMobileToggle();
    
    // Setup article section toggle
    setupArticleToggle();
    
    console.log('[ScrollSpy] Initialization complete');
  }

  /**
   * Add IDs to headings in the content based on TOC links
   * Simple approach: Match by order - Nth heading gets Nth TOC link's ID
   */
  function addHeadingIds(contentArea, tocNav) {
    const headings = contentArea.querySelectorAll('h2, h3, h4');
    const tocLinks = tocNav.querySelectorAll('.rr-article-toc-link[data-heading-id]');
    
    console.log(`[ScrollSpy] Found ${headings.length} headings in content`);
    console.log(`[ScrollSpy] Found ${tocLinks.length} TOC links`);
    
    let idsAdded = 0;
    
    // Match headings with TOC links by order (simpler and more reliable)
    headings.forEach((heading, index) => {
      if (heading.id) {
        console.log(`[ScrollSpy] Heading ${index} already has ID: "${heading.id}"`);
        return;
      }
      
      if (index < tocLinks.length) {
        const tocLink = tocLinks[index];
        const headingId = tocLink.getAttribute('data-heading-id');
        const headingText = heading.textContent.trim();
        
        if (headingId) {
          heading.id = headingId;
          idsAdded++;
          console.log(`[ScrollSpy] ✓ Heading ${index} "${headingText}" → ID: "${headingId}"`);
        }
      } else {
        console.warn(`[ScrollSpy] ✗ No TOC link for heading ${index}: "${heading.textContent.trim()}"`);
      }
    });
    
    console.log(`[ScrollSpy] Added ${idsAdded}/${headings.length} IDs`);
  }

  /**
   * Initialize custom ScrollSpy
   * NOTE: We tried Bootstrap ScrollSpy but the behavior was awful (jumping between sections,
   * inconsistent activation when scrolling up vs down). Built our own that activates based on
   * which element is at the top of the viewport.
   */
  function initScrollSpy(tocNav) {
    // Get header height from CSS variable on :root element
    const rootStyles = getComputedStyle(document.documentElement);
    const headerHeightValue = rootStyles.getPropertyValue('--header-height-above-mega').trim();
    const headerHeight = parseInt(headerHeightValue) || 58; // Default to 58px if not found
    
    // Optional buffer for scroll position adjustment (negative values scroll further down)
    const scrollBuffer = -5;
    
    console.log(`[ScrollSpy] Reading from :root element`);
    console.log(`[ScrollSpy] --header-height-above-mega: ${headerHeight}px`);
    console.log(`[ScrollSpy] Scroll buffer: ${scrollBuffer}px`);
    
    // Get all trackable elements: headings + special sections
    const headings = document.querySelectorAll('.rr-article-content h2, .rr-article-content h3, .rr-article-content h4');
    const specialSections = document.querySelectorAll('#article-media, #article-additional-materials, #article-related-products');
    
    // Combine and sort all tracked elements by position
    const allTrackedElements = [...headings, ...specialSections].sort((a, b) => {
      return a.offsetTop - b.offsetTop;
    });
    
    const tocLinks = tocNav.querySelectorAll('a[href^="#"]');
    
    console.log(`[ScrollSpy] Tracking ${headings.length} headings + ${specialSections.length} sections`);
    console.log(`[ScrollSpy] Total tracked elements: ${allTrackedElements.length}`);
    console.log(`[ScrollSpy] Found ${tocLinks.length} TOC links`);
    
    if (allTrackedElements.length === 0) {
      console.warn('[ScrollSpy] No elements found to track');
      return;
    }
    
    // Throttle scroll events for performance
    let ticking = false;
    
    function updateActiveSection() {
      const scrollPosition = window.scrollY + headerHeight + scrollBuffer;
      
      let currentElement = null;
      
      // Find the last element that's above the scroll position
      allTrackedElements.forEach(element => {
        const elementTop = element.offsetTop;
        if (elementTop <= scrollPosition) {
          currentElement = element;
        }
      });
      
      // Update active states
      if (currentElement && currentElement.id) {
        const activeLink = tocNav.querySelector(`a[href="#${currentElement.id}"]`);
        
        if (activeLink && !activeLink.classList.contains('active')) {
          console.log(`[ScrollSpy] Activating section: #${currentElement.id}`);
          
          // Remove all active classes
          tocLinks.forEach(link => link.classList.remove('active'));
          
          // Add active to current link
          activeLink.classList.add('active');
          
          // Update section toggles
          updateSectionToggles(activeLink);
        }
      }
      
      ticking = false;
    }
    
    function onScroll() {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          updateActiveSection();
        });
        ticking = true;
      }
    }
    
    // Initial update
    updateActiveSection();
    
    // Listen to scroll events
    window.addEventListener('scroll', onScroll, { passive: true });
    console.log('[ScrollSpy] Custom ScrollSpy initialized (Bootstrap ScrollSpy not used - behavior was poor)');
    
    // Store updateActiveSection globally so smooth scroll can call it
    window._scrollSpyUpdate = updateActiveSection;
  }

  /**
   * Update section toggle active states based on active link
   */
  function updateSectionToggles(activeLink) {
    // Remove active from all section toggles
    const allToggles = document.querySelectorAll('.rr-toc-section-toggle');
    allToggles.forEach(toggle => toggle.classList.remove('active'));
    
    // If it's a nested article heading, add active to article toggle and expand section
    if (activeLink.classList.contains('rr-toc-h2') || 
        activeLink.classList.contains('rr-toc-h3') || 
        activeLink.classList.contains('rr-toc-h4')) {
      const articleToggle = document.querySelector('[data-section="article"]');
      if (articleToggle) {
        articleToggle.classList.add('active');
        expandArticleSection();
      }
    } else if (activeLink.classList.contains('rr-toc-section-toggle')) {
      // The active link itself is a section toggle
      activeLink.classList.add('active');
    }
  }

  /**
   * Setup smooth scroll for TOC links with header offset
   */
  function setupSmoothScroll(tocNav) {
    // Get header height from CSS variable on :root element
    const rootStyles = getComputedStyle(document.documentElement);
    const headerHeightValue = rootStyles.getPropertyValue('--header-height-above-mega').trim();
    const headerHeight = parseInt(headerHeightValue) || 58;
    
    // Optional buffer for scroll position adjustment (negative values scroll further down)
    const scrollBuffer = -5;
    
    const tocLinks = tocNav.querySelectorAll('a[href^="#"]');
    
    tocLinks.forEach(link => {
      link.addEventListener('click', (e) => {
        const href = link.getAttribute('href');
        
        // Skip if it's just "#" or empty
        if (!href || href === '#') return;
        
        const targetId = href.substring(1);
        const targetElement = document.getElementById(targetId);
        
        if (targetElement) {
          e.preventDefault();
          
          // Calculate scroll position with header offset
          const elementPosition = targetElement.getBoundingClientRect().top + window.scrollY;
          const offsetPosition = elementPosition - headerHeight - scrollBuffer;
          
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
          
          // Update scrollspy after smooth scroll completes
          // Use scrollend event (modern) with fallback to timeout
          const updateAfterScroll = () => {
            if (window._scrollSpyUpdate) {
              window._scrollSpyUpdate();
            }
          };
          
          // Try modern scrollend event first
          if ('onscrollend' in window) {
            window.addEventListener('scrollend', updateAfterScroll, { once: true });
          } else {
            // Fallback: wait for smooth scroll animation to complete (~500ms typical)
            setTimeout(updateAfterScroll, 600);
          }
        }
      });
    });
    
    console.log(`[ScrollSpy] Smooth scroll enabled for ${tocLinks.length} TOC links with offset: ${headerHeight + scrollBuffer}px`);
  }

  /**
   * Setup mobile TOC toggle
   */
  function setupMobileToggle() {
    const mobileToggle = document.querySelector('.rr-article-toc-mobile-toggle');
    const tocSidebar = document.querySelector('.rr-article-toc-sidebar');
    
    if (!mobileToggle || !tocSidebar) return;
    
    mobileToggle.addEventListener('click', () => {
      const isExpanded = mobileToggle.getAttribute('aria-expanded') === 'true';
      
      if (isExpanded) {
        mobileToggle.setAttribute('aria-expanded', 'false');
        tocSidebar.classList.remove('mobile-expanded');
      } else {
        mobileToggle.setAttribute('aria-expanded', 'true');
        tocSidebar.classList.add('mobile-expanded');
      }
    });
    
    // Close mobile TOC when clicking any link
    const tocLinks = tocSidebar.querySelectorAll('a');
    tocLinks.forEach(link => {
      link.addEventListener('click', () => {
        mobileToggle.setAttribute('aria-expanded', 'false');
        tocSidebar.classList.remove('mobile-expanded');
      });
    });
  }

  /**
   * Setup Article section toggle (expand/collapse)
   */
  function setupArticleToggle() {
    const articleToggle = document.querySelector('[data-section="article"]');
    const nestedNav = document.querySelector('#article-toc-nested');
    
    if (!articleToggle || !nestedNav) return;
    
    // Set initial max-height for smooth transitions
    nestedNav.style.maxHeight = nestedNav.scrollHeight + 'px';
    
    articleToggle.addEventListener('click', (e) => {
      e.preventDefault();
      
      const isCollapsed = articleToggle.classList.contains('collapsed');
      
      if (isCollapsed) {
        articleToggle.classList.remove('collapsed');
        nestedNav.classList.remove('collapsed');
        nestedNav.style.maxHeight = nestedNav.scrollHeight + 'px';
      } else {
        articleToggle.classList.add('collapsed');
        nestedNav.classList.add('collapsed');
        nestedNav.style.maxHeight = '0';
      }
    });
  }

  /**
   * Expand the Article section (called by ScrollSpy)
   */
  function expandArticleSection() {
    const articleToggle = document.querySelector('[data-section="article"]');
    const nestedNav = document.querySelector('#article-toc-nested');
    
    if (!articleToggle || !nestedNav) return;
    
    if (articleToggle.classList.contains('collapsed')) {
      articleToggle.classList.remove('collapsed');
      nestedNav.classList.remove('collapsed');
      nestedNav.style.maxHeight = nestedNav.scrollHeight + 'px';
    }
  }
})();
